import { can, formatDate } from '@clim/client-core';
import type { PermissionDef, Scope, TerritoryRecord } from '@clim/domain';
import { useState } from 'react';
import { ErrorBox, Field, Input, Modal } from '../../components/ui';
import { useReady } from '../../lib/session';
import { useApi, useMutation } from './common';

interface UserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  status: 'active' | 'disabled';
  lastLoginAt: string | null;
  roles: { id: string; code: string; name: string }[];
  territoryIds: string[];
  isContractor: boolean;
  companyName: string | null;
  companySiret: string | null;
  mfaEnabled?: boolean;
}
interface RoleRow {
  id: string;
  code: string;
  name: string;
}

export function UsersAdmin() {
  const { me } = useReady();
  const users = useApi<UserRow[]>('/v1/admin/users');
  const roles = useApi<RoleRow[]>('/v1/admin/roles');
  const territories = useApi<TerritoryRecord[]>('/v1/territories');
  const [editing, setEditing] = useState<UserRow | 'new' | null>(null);
  const [rights, setRights] = useState<UserRow | null>(null);
  const m = useMutation();
  // recherche par nom, e-mail ou société ; tri par société pour retrouver les sous-traitants d'une même entreprise
  const [q, setQ] = useState('');
  const [byCompany, setByCompany] = useState(false);
  const list = (users.data ?? [])
    .filter((u) => !q.trim() || [`${u.firstName} ${u.lastName}`, u.email, u.companyName].some((v) => v?.toLowerCase().includes(q.trim().toLowerCase())))
    .sort((a, b) => (byCompany ? (a.companyName ?? '\uffff').localeCompare(b.companyName ?? '\uffff', 'fr') : 0) || a.lastName.localeCompare(b.lastName, 'fr'));

  return (
    <div>
      <div className="page-head">
        <h2>Utilisateurs</h2>
        {can(me, 'user.manage') && (
          <button className="btn primary" onClick={() => setEditing('new')}>
            + Utilisateur
          </button>
        )}
      </div>
      <ErrorBox message={users.error ?? m.error} />
      <div className="filters">
        <Input type="search" aria-label="Rechercher un membre" placeholder="Rechercher : nom, e-mail, société…" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="check">
          <input type="checkbox" checked={byCompany} onChange={(e) => setByCompany(e.target.checked)} /> Trier par société
        </label>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Rôles</th>
            <th>Territoires</th>
            <th>Dernière connexion</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.map((u) => (
            <tr key={u.id} className={u.status === 'disabled' ? 'disabled' : ''}>
              <td>
                {u.firstName} {u.lastName}
                {u.companyName && <div className="company-line">🏢 {u.companyName}{u.companySiret ? ` · SIRET ${u.companySiret}` : ''}</div>}
                <div className="muted">
                  {u.email}
                  {u.phone ? ` · 📱 ${u.phone}` : ' · pas de portable'}
                </div>
                {u.isContractor && <span className="badge tone-neutral">Sous-traitant</span>}
                {u.mfaEnabled && <span className="badge tone-success">Double vérification</span>}
              </td>
              <td>{u.roles.map((r) => r.name).join(' + ')}</td>
              <td>
                {u.territoryIds.map((id) => territories.data?.find((t) => t.id === id)?.code).join(', ')}
              </td>
              <td>{formatDate(u.lastLoginAt)}</td>
              <td className="nowrap">
                {can(me, 'user.assign_roles') && (
                  <button className="btn small" onClick={() => setEditing(u)}>
                    Modifier (téléphone, rôles…)
                  </button>
                )}
                {can(me, 'user.assign_roles') && u.id !== me.user.id && (
                  <button className="btn small" onClick={() => setRights(u)}>
                    Droits individuels
                  </button>
                )}
                {can(me, 'user.manage') && u.id !== me.user.id && (
                  <button
                    className="btn small"
                    onClick={() =>
                      void m
                        .run(
                          'POST',
                          `/v1/admin/users/${u.id}/${u.status === 'active' ? 'disable' : 'enable'}`,
                        )
                        .then(users.reload)
                    }
                  >
                    {u.status === 'active' ? 'Désactiver' : 'Réactiver'}
                  </button>
                )}
                {can(me, 'user.manage') && u.mfaEnabled && u.id !== me.user.id && (
                  <button
                    className="btn small"
                    onClick={() =>
                      confirm(
                        `Téléphone perdu : retirer la double vérification de ${u.firstName} ${u.lastName} ? Ses sessions seront fermées et il devra la reconfigurer.`,
                      ) && void m.run('POST', `/v1/admin/users/${u.id}/mfa-reset`).then(users.reload)
                    }
                  >
                    Réinitialiser la double vérification
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rights && <OverridesDialog user={rights} onClose={() => setRights(null)} />}
      {editing && roles.data && territories.data && (
        <UserDialog
          user={editing === 'new' ? null : editing}
          roles={roles.data}
          territories={territories.data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            users.reload();
          }}
        />
      )}
    </div>
  );
}

function UserDialog(props: {
  user: UserRow | null;
  roles: RoleRow[];
  territories: TerritoryRecord[];
  onClose(): void;
  onSaved(): void;
}) {
  const { user } = props;
  const m = useMutation();
  const [f, setF] = useState({ email: '', firstName: user?.firstName ?? '', lastName: user?.lastName ?? '', temporaryPassword: '' });
  const [roleIds, setRoleIds] = useState<string[]>(user?.roles.map((r) => r.id) ?? []);
  const [territoryIds, setTerritoryIds] = useState<string[]>(user?.territoryIds ?? []);
  const [isContractor, setIsContractor] = useState(user?.isContractor ?? false);
  // société du sous-traitant (raison sociale, SIRET facultatif)
  const [companyName, setCompanyName] = useState(user?.companyName ?? '');
  const [companySiret, setCompanySiret] = useState(user?.companySiret ?? '');
  // portable : SMS « consultez votre application » (interventions, prospects)
  const [phone, setPhone] = useState(user?.phone ?? '');
  // technicien : voir et encaisser les paiements de SES dossiers, sur option (droits individuels réversibles)
  const overrides = useApi<{ permission: string; mode: 'grant' | 'revoke'; scope: Scope | null }[]>(
    user ? `/v1/admin/users/${user.id}/overrides` : null,
  );
  const PAY = ['payment.read', 'payment.collect'];
  const hasPayments =
    (overrides.data ?? []).filter((o) => PAY.includes(o.permission) && o.mode === 'grant').length === 2;
  const [payments, setPayments] = useState<boolean | null>(null);
  const isTech = props.roles.some((r) => r.code === 'technicien' && roleIds.includes(r.id));
  const toggle = (list: string[], set: (v: string[]) => void, id: string) =>
    set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const save = async () => {
    if (user) {
      await m.run('PUT', `/v1/admin/users/${user.id}/roles`, roleIds);
      await m.run('PUT', `/v1/admin/users/${user.id}/territories`, territoryIds);
      if (isContractor !== user.isContractor)
        await m.run('PATCH', `/v1/admin/users/${user.id}`, { isContractor });
      if (phone.trim() !== (user.phone ?? '')) await m.run('PATCH', `/v1/admin/users/${user.id}`, { phone: phone.trim() || null });
      if (companyName.trim() !== (user.companyName ?? '') || companySiret.replace(/\s/g, '') !== (user.companySiret ?? ''))
        await m.run('PATCH', `/v1/admin/users/${user.id}`, { companyName: companyName.trim() || null, companySiret: companySiret.trim() || null });
      if (f.firstName.trim() !== user.firstName || f.lastName.trim() !== user.lastName)
        await m.run('PATCH', `/v1/admin/users/${user.id}`, { firstName: f.firstName.trim(), lastName: f.lastName.trim() });
      if (payments !== null && payments !== hasPayments) {
        const others = (overrides.data ?? [])
          .filter((o) => !PAY.includes(o.permission))
          .map((o) => (o.mode === 'revoke' ? { permission: o.permission, mode: 'revoke' } : o));
        await m.run('PUT', `/v1/admin/users/${user.id}/overrides`, [
          ...others,
          ...(payments ? PAY.map((permission) => ({ permission, mode: 'grant', scope: 'own' })) : []),
        ]);
      }
    } else {
      await m.run('POST', '/v1/admin/users', {
        ...f,
        phone: phone.trim() || null,
        roleIds,
        territoryIds,
        isContractor,
        companyName: companyName.trim() || null,
        companySiret: companySiret.trim() || null,
      });
    }
    props.onSaved();
  };

  return (
    <Modal title={user ? `${user.firstName} ${user.lastName}` : 'Nouvel utilisateur'} onClose={props.onClose}>
      <div className="form">
        <div className="grid2">
          <Field label="Prénom">
            <Input value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} />
          </Field>
          <Field label="Nom">
            <Input value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} />
          </Field>
        </div>
        {!user && (
          <>
            <Field label="E-mail">
              <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            </Field>
            <Field label="Mot de passe provisoire" hint="À changer à la première connexion">
              <Input
                value={f.temporaryPassword}
                onChange={(e) => setF({ ...f, temporaryPassword: e.target.value })}
              />
            </Field>
          </>
        )}
        <Field label="Téléphone portable (pour les SMS)">
          <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="0690 12 34 56" />
        </Field>
        <div className="grid2">
          <Field label="Société (sous-traitant)" hint="Raison sociale de son entreprise">
            <Input aria-label="Société" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Ex. : SARL Clim Services" />
          </Field>
          <Field label="SIRET (facultatif)">
            <Input aria-label="SIRET de la société" inputMode="numeric" value={companySiret} onChange={(e) => setCompanySiret(e.target.value)} />
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={isContractor} onChange={(e) => setIsContractor(e.target.checked)} />
          Sous-traitant : suivre ses documents professionnels (KBIS, assurance, attestation fluides…)
        </label>
        {user && isTech && overrides.data && (
          <label className="check">
            <input
              type="checkbox"
              checked={payments ?? hasPayments}
              onChange={(e) => setPayments(e.target.checked)}
              aria-label="Voir et encaisser les paiements"
            />
            Technicien : voir les montants et encaisser le client sur ses interventions (si le commercial n’a
            pas pu)
          </label>
        )}
        <fieldset>
          <legend>Rôles (cumulables)</legend>
          {props.roles.map((r) => (
            <label key={r.id} className="check">
              <input
                type="checkbox"
                checked={roleIds.includes(r.id)}
                onChange={() => toggle(roleIds, setRoleIds, r.id)}
              />
              {r.name}
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend>Territoires</legend>
          {props.territories
            .filter((t) => t.isActive)
            .map((t) => (
              <label key={t.id} className="check">
                <input
                  type="checkbox"
                  checked={territoryIds.includes(t.id)}
                  onChange={() => toggle(territoryIds, setTerritoryIds, t.id)}
                />
                {t.name}
              </label>
            ))}
        </fieldset>
        <ErrorBox message={m.error} />
        <div className="actions">
          <button className="btn" onClick={props.onClose}>
            Annuler
          </button>
          <button className="btn primary" onClick={() => void save().catch(() => undefined)}>
            Enregistrer
          </button>
        </div>
      </div>
    </Modal>
  );
}

type OverrideValue = '' | 'revoke' | `grant:${Scope}`;
const SCOPE_LABEL: Record<Scope, string> = { own: 'les siens', territory: 'son territoire', all: 'tout' };

/**
 * Droits individuels (CDC §2 : superviseur « ajustable individuellement », droits « retirables
 * individuellement »). Hérité = ce que donnent les rôles ; Accordé / Retiré = exception tracée.
 */
function OverridesDialog({ user, onClose }: { user: UserRow; onClose(): void }) {
  const perms = useApi<PermissionDef[]>('/v1/admin/permissions');
  const current = useApi<{ permission: string; mode: 'grant' | 'revoke'; scope: Scope | null }[]>(
    `/v1/admin/users/${user.id}/overrides`,
  );
  const m = useMutation();
  const [draft, setDraft] = useState<Record<string, OverrideValue> | null>(null);
  const values: Record<string, OverrideValue> =
    draft ??
    Object.fromEntries(
      (current.data ?? []).map((o) => [
        o.permission,
        o.mode === 'revoke' ? 'revoke' : (`grant:${o.scope}` as OverrideValue),
      ]),
    );
  const save = async () => {
    const list = Object.entries(values)
      .filter(([, v]) => v)
      .map(([permission, v]) =>
        v === 'revoke'
          ? { permission, mode: 'revoke' }
          : { permission, mode: 'grant', scope: v.split(':')[1] },
      );
    await m.run('PUT', `/v1/admin/users/${user.id}/overrides`, list);
    onClose();
  };
  const active = (perms.data ?? []).filter((p) => p.status === 'active');
  return (
    <Modal title={`Droits individuels — ${user.firstName} ${user.lastName}`} onClose={onClose}>
      <p className="muted">
        Rôles : {user.roles.map((r) => r.name).join(' + ') || 'aucun'}. Laisser « Hérité » pour appliquer les
        rôles.
      </p>
      <table className="table compact">
        <tbody>
          {active.map((p) => (
            <tr key={p.code}>
              <td>{p.label}</td>
              <td>
                <select
                  className="input"
                  aria-label={p.label}
                  value={values[p.code] ?? ''}
                  onChange={(e) => setDraft({ ...values, [p.code]: e.target.value as OverrideValue })}
                >
                  <option value="">Hérité des rôles</option>
                  <option value="revoke">Retiré</option>
                  {(p.scoped ? (['own', 'territory', 'all'] as Scope[]) : (['all'] as Scope[])).map((s) => (
                    <option key={s} value={`grant:${s}`}>
                      Accordé ({SCOPE_LABEL[s]})
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ErrorBox message={m.error ?? perms.error ?? current.error} />
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Annuler
        </button>
        <button className="btn primary" onClick={() => void save().catch(() => undefined)}>
          Enregistrer
        </button>
      </div>
    </Modal>
  );
}
